import type { ReactNode } from 'react';
import { Tooltip } from '@mantine/core';
import { tx, useLang } from '../i18n';
import { IconInfo } from '../icons';
import classes from './ui.module.css';

/** Цвета статусов (одинаковые в таблицах, на экранах и в предпросмотре). */
export const STATUS_TONE: Record<string, Tone> = {
  Открыт: 'blue',
  'Частично выполнен': 'amber',
  Выполнен: 'green',
  Просрочен: 'red',
  Отменено: 'gray',
  'Ожидает закупки': 'gray',
  'Частично закуплено': 'amber',
  Закуплено: 'green',
  Излишек: 'violet'
};

export type Tone = 'blue' | 'green' | 'amber' | 'red' | 'violet' | 'gray' | 'accent';

/** Метка статуса: точка и текст на мягкой подложке. */
export function StatusPill({ status, tone }: { status: string; tone?: Tone }) {
  useLang();
  return (
    <span className={classes.pill} data-tone={tone ?? STATUS_TONE[status] ?? 'gray'}>
      <span className={classes.pillDot} />
      {tx(status)}
    </span>
  );
}

const AVATAR_TONES = ['#6b6bfa', '#14b562', '#e08a0b', '#e0514a', '#9b6bff', '#1f93c9', '#c2539a'];

function hash(text: string): number {
  let h = 0;
  for (let i = 0; i < text.length; i++) h = (h * 31 + text.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** Инициалы для кружка автора. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 1).toUpperCase();
  return (parts[0]!.slice(0, 1) + parts[1]!.slice(0, 1)).toUpperCase();
}

export function avatarColor(name: string): string {
  return AVATAR_TONES[hash(name) % AVATAR_TONES.length]!;
}

/** Кружок с инициалами (автор, роль). */
export function Avatar({ name, size = 24 }: { name: string; size?: number }) {
  return (
    <span
      className={classes.avatar}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.42), background: avatarColor(name) }}
      aria-hidden
    >
      {initials(name)}
    </span>
  );
}

/** Подпись поля с подсказкой во всплывающем окне — вместо строки мелкого текста под каждым полем. */
export function FieldLabel({ text, hint }: { text: string; hint?: string }) {
  if (!hint) return <>{text}</>;
  return (
    <span className={classes.fieldLabel}>
      {text}
      <Tooltip label={hint} multiline w={280} position="top-start">
        <span className={classes.fieldHint} tabIndex={0} aria-label={hint}>
          <IconInfo size={13} />
        </span>
      </Tooltip>
    </span>
  );
}

/** Показатель в строке итогов: подпись и значение. */
export function Metric({ label, value, tone }: { label: string; value: ReactNode; tone?: Tone }) {
  return (
    <span className={classes.metric} data-tone={tone}>
      <span className={classes.metricLabel}>{label}</span>
      <span className={classes.metricValue}>{value}</span>
    </span>
  );
}

/** Узкая полоса прогресса (доля 0…1). */
export function Progress({ value, tone = 'accent', width = 64 }: { value: number; tone?: Tone; width?: number }) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <span className={classes.progress} style={{ width }} data-tone={tone}>
      <span style={{ width: `${pct}%` }} />
    </span>
  );
}

/** Русское склонение: plural(5, 'позиция', 'позиции', 'позиций'). */
export function plural(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
}
